import { computed } from "vue";
import { defineStore } from "pinia";
import { useUser } from "./user.store";

export const useLicense = defineStore("license", () => {
  const { user } = useUser();

  const licenses = computed(() => {
    const list: {
      id: string;
      plan: string;
      expired: boolean;
      validUntil: string | null;
      providedBy: { slug: string; name: string; subscriptionId: number } | null;
      permissions: { canPublish: boolean; canSwitchScenes: boolean };
    }[] = [];

    if (!user.value) return list;

    if (user.value.licenses) {
      for (const license of user.value.licenses) {
        list.push({
          id: license.id,
          plan: license.plan,
          expired:
            license.validUntil == null
              ? false
              : new Date(license.validUntil) < new Date(),
          validUntil: license.validUntil,
          providedBy: license.providedBy,
          permissions: license.permissions
        });
      }
    }

    // move expired licenses to the end
    list.sort((a, b) => {
      return a.expired === b.expired ? 0 : a.expired ? 1 : -1;
    });

    return list;
  });

  const nonExpiredLicenses = computed(() => {
    return licenses.value.filter((l) => !l.expired);
  });

  /**
   * get the things the user can't do
   */
  const limits = computed(
    (): {
      maxModels?: number;
      canDownload: boolean;
      canImport: boolean;
      canShare: boolean;
      canPublish: boolean;
      canSwitchScenes: boolean;
    } => {
      if (nonExpiredLicenses.value.length == 0) {
        return {
          maxModels: 1,
          canDownload: true,
          canImport: true,
          canShare: false,
          canPublish: false,
          canSwitchScenes: false
        };
      }

      const limits: {
        maxModels?: number;
        canDownload: boolean;
        canImport: boolean;
        canShare: boolean;
        canPublish: boolean;
        canSwitchScenes: boolean;
      } = {
        canDownload: true,
        canImport: true,
        canShare: true,
        canPublish: false,
        canSwitchScenes: false
      };

      if (user.value?.isPublisher) {
        limits.canPublish = true;
      }
      if (user.value?.isSamplemodelAdmin) {
        limits.canPublish = true;
      }

      for (const license of nonExpiredLicenses.value) {
        if (license.permissions.canPublish) {
          limits.canPublish = true;
        }
        if (license.permissions.canSwitchScenes) {
          limits.canSwitchScenes = true;
        }
      }

      return limits;
    }
  );

  return {
    limits,
    nonExpiredLicenses,
    all: licenses
  };
});
